import { format } from 'date-fns';
import _ from 'lodash';
import { IGerenciadorPrecosDatapoint as RowData, UnknownObject } from '../../../../@types';
import { GerenciadorPrecos } from '../../../../@types/GerenciadorPrecos';
import { Sort, SortPriceType } from '../../../../@types/sort';
import m from '../../../../lib/IPAMaths';
import { DatapointsCacheState } from '../../../../reducers/gerenciadorPrecos/datapointsCache';
import { FiltersValueState } from '../../../../reducers/gerenciadorPrecos/filters/filtersValues';
import { MoreFiltersState } from '../../../../reducers/gerenciadorPrecos/filters/moreFilters';
import { ADJUSTED_PRICE_BY_LIMIT, LimitFilterValue, PriceManagerQuickActionFilter } from '../../../../reducers/gerenciadorPrecos/quickActionsFilter';
import { ApplyPricesParams, UndoEditedDatapoint } from '../services';
import { getSortedData } from './getSortedData';
import { getValidData } from './getValidData';

export const handleCalculoPreco = (price: number, rowData: RowData) => {
  const { competitorsPrice, salesTaxPercentage, expensesPercent, cost } = rowData;
  const novaCompetitividade = m.novaCompetitividade(price, competitorsPrice);
  const novaMargem = m.novaMargem(price, salesTaxPercentage, expensesPercent, cost);
  return {
    price,
    novaCompetitividade,
    novaMargem,
  } as const;
};

export const handleCalculoCompetitividade = (novaCompetitividade: number, rowData: RowData) => {
  const { competitorsPrice, salesTaxPercentage, expensesPercent, cost } = rowData;
  const price = m.precoWithCpi(novaCompetitividade, competitorsPrice);
  const novaMargem = m.novaMargem(price, salesTaxPercentage, expensesPercent, cost);
  return {
    novaCompetitividade,
    novaMargem,
    price,
  } as const;
};

export const handleCalculoMargem = (novaMargem: number, rowData: RowData) => {
  const { cost, salesTaxPercentage, expensesPercent, competitorsPrice } = rowData;

  const price = m.precoWithMargem(novaMargem, cost, expensesPercent, salesTaxPercentage);

  const novaCompetitividade = m.cpiWithMargem(novaMargem, price, competitorsPrice);

  return {
    novaMargem,
    price,
    novaCompetitividade,
  } as const;
};

export const GET_MODEL_GERENCIADOR_FILTROS = (filters: FiltersValueState) => {
  const getSensibility = (type: GerenciadorPrecos.Sensibilities) => {
    return filters.segmentos?.filter(item => item?.type === type).map(item => item?.value) || null;
  };

  const parsePriceStatus = (data: FiltersValueState['priceStatus'] = []): { key: string; status: string[] }[] => {
    const result = Object.values(
      data.reduce(
        (acc, { type, value }) => {
          if (!acc[type]) acc[type] = { key: type, status: [] };
          acc[type].status.push(value);
          return acc;
        },
        {} as Record<string, { key: string; status: string[] }>,
      ),
    );

    return result;
  };

  const priceStatusFilter = parsePriceStatus(filters.priceStatus);

  const data = {
    productIds: filters.productIds,
    storeIds: filters.storeIds,
    productFamilyIds: filters.productFamilyIds,
    supplier: filters.supplier,
    productBrand: filters.productBrand,
    categoryLevel1: filters.categoryLevel1,
    categoryLevel2: filters.categoryLevel2,
    categoryLevel3: filters.categoryLevel3,
    categoryLevel4: filters.categoryLevel4,
    categoryLevel5: filters.categoryLevel5,
    categoryLevel6: filters.categoryLevel6,
    categoryLevel7: filters.categoryLevel7,
    sensibilities: getSensibility('infoPriceSensibility'),
    exclusive: getSensibility('sensibilityType'),
    curveAbcLocal: getSensibility('infoPriceAbcClass'),
    curveAbcGlobal: getSensibility('infoPriceGlobalAbcClass'),
    curve: getSensibility('abcClass'),
    clusters: filters.clusters,
    storeType: filters.storeTypes,
    priceStatusFilter,
  } as const;

  return getValidData<typeof data>(data);
};

export const GET_MODEL_GERENCIADOR_SORT = (sort: Sort, sortColumnsTypes?: { name: string }) => {
  return getValidData({
    sort: getSortedData({ sort, sortColumnsTypes }),
  });
};

export const GET_MODEL_GERENCIADOR_PAGINATION = ({ displayLength = 25, activePage = 0 }: GerenciadorPrecos.Table.Pagination) => {
  return {
    size: displayLength,
    page: activePage < 0 ? 0 : activePage,
  } as const;
};

const getMinDate = (lastDays: unknown) => {
  const today = new Date(Date.now());
  const day = format(
    // @ts-ignore
    new Date(today.getTime() - +lastDays * 24 * 60 * 60 * 1000),
    'yyyy-MM-dd',
  );
  return day;
};

export const shouldSendOfferDataParam = ({ isOfferQuickFilterActive }: { isOfferQuickFilterActive: boolean }) => {
  return isOfferQuickFilterActive;
};

type LimitFilterParams = {
  brokenLimit: boolean | null;
  adjustedPriceByLimit?: true;
};

// O backend só aplica adjustedPriceByLimit sobre o preço regular, mesmo com priceTypeFilter em OFFER:
// a conformação pelo limite não existe do lado oferta, então não há direção a mandar aqui.
export const getLimitFilterParams = ({ isActive, value }: PriceManagerQuickActionFilter['brokenLimit']): LimitFilterParams => {
  if (!isActive) return { brokenLimit: null };
  if (value === ADJUSTED_PRICE_BY_LIMIT) return { brokenLimit: null, adjustedPriceByLimit: true };
  return { brokenLimit: value };
};

// Inverso de getLimitFilterParams: devolve a opção do filtro de limites a partir dos parâmetros
// gravados num filtro salvo, ou null quando o filtro salvo não tem filtro de limites.
export const getLimitFilterValueFromParams = ({
  brokenLimit,
  adjustedPriceByLimit,
}: {
  brokenLimit?: unknown;
  adjustedPriceByLimit?: unknown;
}): LimitFilterValue | null => {
  if (adjustedPriceByLimit === true) return ADJUSTED_PRICE_BY_LIMIT;
  if (typeof brokenLimit === 'boolean') return brokenLimit;
  return null;
};

export const GET_MODEL_GERENCIADOR_QUICK_ACTION_FILTERS = (
  {
    brokenLimit,
    changedPrices,
    competitorPrices,
    minDateCompetitorPrice,
    isProductMaster,
    marginRange,
    priceVariationRange,
    cpiRange,
    changedCosts,
    stockQuantityFilter,
    isOfferData,
    priceTypeFilterIsActive,
    priceTypeFilter,
  }: PriceManagerQuickActionFilter,
  sortPriceType: SortPriceType = 'REGULAR',
): UnknownObject => {
  const { isActive, ...marginRangeObj } = marginRange;
  const { isActive: isActivePriceVariation, ...priceVariationRangeObj } = priceVariationRange;
  const { isActive: isActiveCompetitiveness, ...competitivenessRangeObj } = cpiRange;

  const data = {
    ...getLimitFilterParams(brokenLimit),
    changedPrices: changedPrices.isActive || null,
    competitorPrices: competitorPrices.isActive,
    priceType: sortPriceType,
    minDateCompetitorPrice: minDateCompetitorPrice.isActive ? getMinDate(minDateCompetitorPrice.value) : null,
    ...(isProductMaster.isActive && {
      isProductMaster: isProductMaster.isActive,
    }),
    competitorPriceSources: minDateCompetitorPrice.competitorPriceSources,
    marginRange: isActive ? marginRangeObj : null,
    priceVariationRange: isActivePriceVariation ? priceVariationRangeObj : null,
    cpiRange: isActiveCompetitiveness ? competitivenessRangeObj : null,
    ...(changedCosts.isActive && {
      changedCost: {
        costChangeDays: changedCosts.value,
        costChangeMinPct: changedCosts.minPct,
        directionUp: changedCosts.directionUp,
        directionDown: changedCosts.directionDown,
      },
    }),
    ...(stockQuantityFilter.isActive && {
      stock: { quantity: stockQuantityFilter.value },
    }),
    ...(shouldSendOfferDataParam({ isOfferQuickFilterActive: isOfferData.isActive }) && {
      isOfferData: true,
    }),
    ...(priceTypeFilterIsActive && {
      priceTypeFilter: priceTypeFilter ?? 'REGULAR',
    }),
  } as const;
  return getValidData<typeof data>(data);
};

namespace ModelApplyPrices {
  export type Params = {
    datapointExhibitionType: string;
    cachedDatapoints: DatapointsCacheState;
    selectedAll: boolean;
    applyToFamily: boolean;
    filters: FiltersValueState;
    moreFilters: MoreFiltersState['render']['values'];
    quickActionsFilter: PriceManagerQuickActionFilter;
    sortPriceType?: SortPriceType;
    effectiveStartDate?: string;
  };
  export type Response = ApplyPricesParams;
}
export const CREATE_MODEL_APPLY_PRICES = ({
  datapointExhibitionType,
  cachedDatapoints,
  selectedAll,
  applyToFamily,
  quickActionsFilter,
  filters,
  moreFilters,
  sortPriceType = 'REGULAR',
  effectiveStartDate,
}: ModelApplyPrices.Params): ModelApplyPrices.Response => {
  const productsSelect = cachedDatapoints.selectedIds.map(item => ({
    productId: item.productId,
    storeId: item.storeId,
    price: item.price,
    productsToBePricedId: item.productsToBePricedId,
  }));

  const productsUnSelect = cachedDatapoints.excludedIds.map(item => ({
    productId: item.productId,
    storeId: item.storeId,
    price: item.price,
    productsToBePricedId: item.productsToBePricedId,
  }));

  const finalApplyToFamily = datapointExhibitionType === 'PRODUCT_CLUSTER' ? false : applyToFamily;

  return {
    productsSelect,
    productsUnSelect,
    selectedAllProducts: selectedAll,
    applyToFamily: finalApplyToFamily,
    datapointExhibitionType: datapointExhibitionType as 'PRODUCT' | 'FAMILY' | 'PRODUCT_CLUSTER' | 'PRODUCT_CLUSTER_FAMILY',
    ...(selectedAll && {
      filters: {
        ...GET_MODEL_GERENCIADOR_FILTROS(filters),
        ...GET_MODEL_GERENCIADOR_QUICK_ACTION_FILTERS(quickActionsFilter, sortPriceType),
        ...moreFilters,
      },
    }),
    ...(effectiveStartDate && { effectiveStartDate }),
  };
};

export const CREATE_MODEL_UNDO_DATAPOINT_PRICE_CHANGE = (
  contextMenu: GerenciadorPrecos.RowData,
  itemsCountFamily: number,
  datapointsExibitionType?: string,
): UndoEditedDatapoint.Params | any => {
  const datapointExhibitionType = datapointsExibitionType ?? 'PRODUCT';

  return {
    ..._.pick(contextMenu, ['productId', 'storeId', 'productsToBePricedId']),
    itemsCountFamily,
    datapointExhibitionType,
  };
};

// Filtros que não são listas e decidem sozinhos se o conjunto está vazio.
const SCALAR_FILTER_IS_EMPTY: Record<string, (value: unknown) => boolean> = {
  changedPrices: value => !value,
  brokenLimit: () => false,
  adjustedPriceByLimit: () => false,
};

export const isEmptyFilters = (filters: FiltersValueState & (MoreFiltersState['render']['values'] | undefined)) => {
  return Object.entries(filters ?? {}).reduce((isEmpty, [key, value]) => {
    if (Array.isArray(value) && value.length !== 0) return false;
    const isScalarEmpty = SCALAR_FILTER_IS_EMPTY[key];
    return isScalarEmpty ? isScalarEmpty(value) : isEmpty;
  }, true);
};

export const isSelectedDatapointsEmpty = (selectedDatapoints: GerenciadorPrecos.SelectedDatapoints) => {
  const { selectedAll, excludedIds, selectedIds } = selectedDatapoints;
  const currentState = selectedAll ? excludedIds : selectedIds;
  const isEmpty = !(!!currentState.length || selectedAll);
  return isEmpty;
};

export const getTotalProducts = (
  // eslint-disable-next-line default-param-last
  totalElements = 0,
  selectedDatapoints: GerenciadorPrecos.SelectedDatapoints,
) => {
  const { selectedAll, excludedIds, selectedIds } = selectedDatapoints;

  let total = totalElements;

  if (selectedAll) {
    total = totalElements - excludedIds.length;
  } else if (!selectedAll && selectedIds.length) {
    total = selectedIds.length;
  }

  return total;
};
